import React, { useState } from "react";
import HandleLogin from "../hooks/handleLogin";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AxiosPostMethod } from "../helpers/customAxios";
import { ErrorToast, SuccessToast } from "../components/toastify";
import { setShowPreloader } from "../store/activePage";
import {
  FaArrowLeft,
  FaArrowRight,
  FaBuilding,
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaMobileAlt,
  FaShieldAlt,
} from "react-icons/fa";

const forgotPasswordEndpoints = {
  sendOtp: "org/forgot-password/send-otp",
  reset: "org/forgot-password/reset",
};

const theme = {
  accent: "#14b8a6",
  accentDark: "#0f766e",
  accentSoft: "#99f6e4",
  panelBorder: "rgba(125, 211, 199, 0.16)",
};

const inputClass = (hasError) =>
  `w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 ${
    hasError
      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
      : "border-slate-200 focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
  }`;

const FieldError = ({ message }) =>
  message ? <p className="mt-1.5 text-xs font-semibold text-red-600">{message}</p> : null;

const LoginForm = () => {
  const Logo = require("../assets/images/logo.jpeg");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [resetUsername, setResetUsername] = useState("");
  const [maskedMobile, setMaskedMobile] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Email or Mobile is required"),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: (values) => {
      HandleLogin(dispatch, navigate, values);
    },
  });

  const requestOtpFormik = useFormik({
    initialValues: {
      username: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Email or Mobile is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      dispatch(setShowPreloader(true));
      const res = await AxiosPostMethod(
        forgotPasswordEndpoints.sendOtp,
        { username: values.username },
        true,
      );
      dispatch(setShowPreloader(false));
      setSubmitting(false);

      if (res?.status === 200) {
        setResetUsername(values.username);
        setMaskedMobile(res?.data?.data?.mobile || "");
        setOtpRequested(true);
        SuccessToast(res?.data?.message || "OTP sent successfully");
        return;
      }

      ErrorToast(res?.data?.message || "Unable to send OTP");
    },
  });

  const resetPasswordFormik = useFormik({
    initialValues: {
      otp: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .length(6, "OTP must be exactly 6 digits")
        .matches(/^\d{6}$/, "OTP must only contain numbers")
        .required("OTP is required"),
      newPassword: Yup.string()
        .min(6, "New password must be at least 6 characters")
        .required("New password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords must match")
        .required("Please confirm your new password"),
    }),
    onSubmit: async (values, { resetForm, setSubmitting, setFieldError }) => {
      dispatch(setShowPreloader(true));
      const res = await AxiosPostMethod(
        forgotPasswordEndpoints.reset,
        {
          username: resetUsername,
          otp: values.otp,
          new_password: values.newPassword,
        },
        true,
      );
      dispatch(setShowPreloader(false));
      setSubmitting(false);

      if (res?.status === 200) {
        SuccessToast(res?.data?.message || "Password reset successfully");
        resetForm();
        requestOtpFormik.resetForm();
        setShowForgotPassword(false);
        setOtpRequested(false);
        setResetUsername("");
        setMaskedMobile("");
        return;
      }

      if (res?.status === 400) {
        setFieldError("otp", res?.data?.message || "Invalid OTP");
      }
      ErrorToast(res?.data?.message || "Unable to reset password");
    },
  });

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setOtpRequested(false);
    setResetUsername("");
    setMaskedMobile("");
    requestOtpFormik.resetForm();
    resetPasswordFormik.resetForm();
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#eef4f8_0%,#f8fbfd_45%,#eef5f7_100%)]">
      <div className="grid min-h-screen lg:grid-cols-[0.95fr_1.05fr]">
        <aside
          className="hidden px-10 py-10 text-white lg:flex lg:flex-col lg:justify-between"
          style={{
            background:
              "linear-gradient(180deg, #0f172a 0%, #132238 48%, #0f1d30 100%)",
            borderRight: `1px solid ${theme.panelBorder}`,
            boxShadow: "8px 0 32px rgba(15,23,42,0.16)",
          }}
        >
          <div>
            <div className="flex items-center gap-3">
              <img
                src={Logo}
                alt="Digital Moi Tech"
                className="h-12 w-12 rounded-xl object-cover ring-2 ring-teal-400/30"
              />
              <div>
                <p className="text-lg font-bold">Digital Moi Tech</p>
                <p className="text-sm text-teal-300/80">Enterprise Portal</p>
              </div>
            </div>

            <div className="mt-20 max-w-md">
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-teal-300/90">
                Secure operations
              </p>
              <h1 className="mt-4 text-4xl font-black leading-tight tracking-tight">
                Manage events, customers, and payments from one workspace.
              </h1>
              <p className="mt-5 text-base leading-7 text-slate-300">
                Sign in to continue event setup, customer onboarding, wallet
                activity, and field operations.
              </p>
            </div>
          </div>

          <div className="grid gap-3">
            {[
              { icon: FaBuilding, text: "Organisation dashboard access" },
              { icon: FaShieldAlt, text: "Protected account workflows" },
              { icon: FaMobileAlt, text: "OTP assisted password recovery" },
            ].map(({ icon: Icon, text }) => (
              <div
                key={text}
                className="flex items-center gap-3 rounded-xl border px-4 py-3"
                style={{
                  borderColor: theme.panelBorder,
                  background: "rgba(20,184,166,0.08)",
                }}
              >
                <Icon className="text-teal-300" />
                <span className="text-sm font-semibold text-slate-50">
                  {text}
                </span>
              </div>
            ))}
          </div>
        </aside>

        <main className="flex items-center justify-center px-4 py-10 sm:px-6 lg:px-12">
          <div className="w-full max-w-md">
            <div className="mb-8 flex items-center gap-3 lg:hidden">
              <img
                src={Logo}
                alt="Digital Moi Tech"
                className="h-11 w-11 rounded-xl object-cover ring-2 ring-teal-200"
              />
              <div>
                <p className="font-bold text-slate-950">Digital Moi Tech</p>
                <p className="text-sm text-teal-700">Enterprise Portal</p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/70 sm:p-8">
              {!showForgotPassword ? (
                <form onSubmit={formik.handleSubmit}>
                  <div className="mb-7">
                    <p className="text-sm font-bold uppercase tracking-[0.2em] text-teal-700">
                      Welcome back
                    </p>
                    <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
                      Sign in
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-slate-500">
                      Use your registered mobile number or email to access your
                      enterprise account.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="username"
                        className="mb-1.5 block text-sm font-bold text-slate-700"
                      >
                        Email or mobile
                      </label>
                      <input
                        type="text"
                        id="username"
                        name="username"
                        value={formik.values.username}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`no-translate ${inputClass(
                          formik.touched.username && formik.errors.username,
                        )}`}
                        placeholder="Enter email or mobile"
                        autoComplete="username"
                      />
                      <FieldError
                        message={
                          formik.touched.username && formik.errors.username
                        }
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="password"
                        className="mb-1.5 block text-sm font-bold text-slate-700"
                      >
                        Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          id="password"
                          name="password"
                          value={formik.values.password}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`no-translate pr-11 ${inputClass(
                            formik.touched.password && formik.errors.password,
                          )}`}
                          placeholder="Enter password"
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-3 flex items-center text-slate-500 transition hover:text-slate-900"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                        >
                          {showPassword ? (
                            <FaEyeSlash size={18} />
                          ) : (
                            <FaEye size={18} />
                          )}
                        </button>
                      </div>
                      <FieldError
                        message={
                          formik.touched.password && formik.errors.password
                        }
                      />
                    </div>
                  </div>

                  <div className="mt-3 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        requestOtpFormik.setFieldValue(
                          "username",
                          formik.values.username,
                        );
                        setShowForgotPassword(true);
                      }}
                      className="text-sm font-bold text-teal-700 transition hover:text-teal-900"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-bold text-white transition hover:bg-teal-800"
                    style={{
                      background:
                        "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)",
                      boxShadow: "0 18px 32px rgba(20,184,166,0.18)",
                    }}
                  >
                    Log In
                    <FaArrowRight className="text-xs" />
                  </button>

                  <p className="mt-6 text-center text-sm text-slate-600">
                    New enterprise?{" "}
                    <Link
                      to="/register"
                      className="font-bold text-teal-700 no-underline hover:text-teal-900"
                    >
                      Create an account
                    </Link>
                  </p>
                </form>
              ) : (
                <form
                  onSubmit={
                    otpRequested
                      ? resetPasswordFormik.handleSubmit
                      : requestOtpFormik.handleSubmit
                  }
                >
                  <div className="mb-7">
                    <button
                      type="button"
                      onClick={closeForgotPassword}
                      className="mb-5 inline-flex items-center gap-2 text-sm font-bold text-slate-500 transition hover:text-slate-900"
                    >
                      <FaArrowLeft className="text-xs" />
                      Back to login
                    </button>
                    <p className="text-sm font-bold uppercase tracking-[0.2em] text-teal-700">
                      Account recovery
                    </p>
                    <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
                      Reset password
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-slate-500">
                      {otpRequested
                        ? `Enter the 6-digit OTP sent to ${maskedMobile || "your registered mobile"}.`
                        : "Enter your registered email or mobile number to receive an OTP."}
                    </p>
                  </div>

                  {!otpRequested ? (
                    <div>
                      <label
                        htmlFor="forgotUsername"
                        className="mb-1.5 block text-sm font-bold text-slate-700"
                      >
                        Email or mobile
                      </label>
                      <input
                        type="text"
                        id="forgotUsername"
                        name="username"
                        value={requestOtpFormik.values.username}
                        onChange={requestOtpFormik.handleChange}
                        onBlur={requestOtpFormik.handleBlur}
                        className={`no-translate ${inputClass(
                          requestOtpFormik.touched.username &&
                            requestOtpFormik.errors.username,
                        )}`}
                        placeholder="Enter email or mobile"
                        autoComplete="username"
                      />
                      <FieldError
                        message={
                          requestOtpFormik.touched.username &&
                          requestOtpFormik.errors.username
                        }
                      />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label
                          htmlFor="otp"
                          className="mb-1.5 block text-sm font-bold text-slate-700"
                        >
                          OTP
                        </label>
                        <input
                          type="text"
                          id="otp"
                          name="otp"
                          value={resetPasswordFormik.values.otp}
                          onChange={resetPasswordFormik.handleChange}
                          onBlur={resetPasswordFormik.handleBlur}
                          className={inputClass(
                            resetPasswordFormik.touched.otp &&
                              resetPasswordFormik.errors.otp,
                          )}
                          placeholder="Enter 6-digit OTP"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                        />
                        <FieldError
                          message={
                            resetPasswordFormik.touched.otp &&
                            resetPasswordFormik.errors.otp
                          }
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="newPassword"
                          className="mb-1.5 block text-sm font-bold text-slate-700"
                        >
                          New password
                        </label>
                        <input
                          type="password"
                          id="newPassword"
                          name="newPassword"
                          value={resetPasswordFormik.values.newPassword}
                          onChange={resetPasswordFormik.handleChange}
                          onBlur={resetPasswordFormik.handleBlur}
                          className={inputClass(
                            resetPasswordFormik.touched.newPassword &&
                              resetPasswordFormik.errors.newPassword,
                          )}
                          placeholder="Enter new password"
                          autoComplete="new-password"
                        />
                        <FieldError
                          message={
                            resetPasswordFormik.touched.newPassword &&
                            resetPasswordFormik.errors.newPassword
                          }
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="confirmPassword"
                          className="mb-1.5 block text-sm font-bold text-slate-700"
                        >
                          Confirm password
                        </label>
                        <input
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                          value={resetPasswordFormik.values.confirmPassword}
                          onChange={resetPasswordFormik.handleChange}
                          onBlur={resetPasswordFormik.handleBlur}
                          className={inputClass(
                            resetPasswordFormik.touched.confirmPassword &&
                              resetPasswordFormik.errors.confirmPassword,
                          )}
                          placeholder="Confirm new password"
                          autoComplete="new-password"
                        />
                        <FieldError
                          message={
                            resetPasswordFormik.touched.confirmPassword &&
                            resetPasswordFormik.errors.confirmPassword
                          }
                        />
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={
                      otpRequested
                        ? resetPasswordFormik.isSubmitting
                        : requestOtpFormik.isSubmitting
                    }
                    className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-bold text-white transition hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-60"
                    style={{
                      background:
                        "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)",
                      boxShadow: "0 18px 32px rgba(20,184,166,0.18)",
                    }}
                  >
                    <FaLock className="text-xs" />
                    {otpRequested
                      ? resetPasswordFormik.isSubmitting
                        ? "Resetting..."
                        : "Reset Password"
                      : requestOtpFormik.isSubmitting
                        ? "Sending..."
                        : "Send OTP"}
                  </button>
                </form>
              )}
            </div>

            <div className="mt-5 flex items-center justify-center gap-2 text-xs font-semibold text-slate-500">
              <FaCheckCircle className="text-teal-600" />
              Secure enterprise access for registered organisations
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default LoginForm;
